import { z } from "zod";
import {
	DeleteSystemSetting200ResponseSchema,
	DeleteSystemSetting400ResponseSchema,
	DeleteSystemSetting404ResponseSchema,
	DeleteSystemSetting500ResponseSchema,
	DeleteSystemSettingPathParamsSchema,
	GetFavicon200ResponseSchema,
	GetFavicon500ResponseSchema,
	GetLogo200ResponseSchema,
	GetLogo500ResponseSchema,
	GetPublicSettings200ResponseSchema,
	GetPublicSettings500ResponseSchema,
	GetStorePic200ResponseSchema,
	GetStorePic500ResponseSchema,
	GetSystemSetting200ResponseSchema,
	GetSystemSetting404ResponseSchema,
	GetSystemSetting500ResponseSchema,
	// Request and Parameter Schemas
	GetSystemSettingPathParamsSchema,
	// Success Response Schemas
	GetSystemSettings200ResponseSchema,
	// Error Response Schemas
	GetSystemSettings500ResponseSchema,
	UpdateBulkSystemSettings200ResponseSchema,
	UpdateBulkSystemSettings400ResponseSchema,
	UpdateBulkSystemSettings500ResponseSchema,
	UpdateBulkSystemSettingsRequestSchema,
	UpdateSystemSetting200ResponseSchema,
	UpdateSystemSetting400ResponseSchema,
	UpdateSystemSetting500ResponseSchema,
	UpdateSystemSettingPathParamsSchema,
	UpdateSystemSettingRequestSchema,
	UploadFavicon200ResponseSchema,
	UploadFavicon400ResponseSchema,
	UploadFavicon500ResponseSchema,
	UploadLogo200ResponseSchema,
	UploadLogo400ResponseSchema,
	UploadLogo500ResponseSchema,
	UploadStorePic200ResponseSchema,
	UploadStorePic400ResponseSchema,
	UploadStorePic500ResponseSchema,
} from "../schemas/systemSettingsApiSchemas";

/**
 * =============================================================================
 * SYSTEM SETTINGS CONTRACTS
 * =============================================================================
 */

/**
 * Get All System Settings contract
 * GET /system-settings
 */
export const getSystemSettingsContract = {
	path: "/system-settings",
	contract: {
		method: "GET" as const,
		responses: {
			200: GetSystemSettings200ResponseSchema,
			500: GetSystemSettings500ResponseSchema,
		},
	},
} as const;

/**
 * Get System Setting by Name contract
 * GET /system-settings/:name
 */
export const getSystemSettingContract = {
	path: "/system-settings/:name",
	contract: {
		method: "GET" as const,
		params: GetSystemSettingPathParamsSchema,
		responses: {
			200: GetSystemSetting200ResponseSchema,
			404: GetSystemSetting404ResponseSchema,
			500: GetSystemSetting500ResponseSchema,
		},
	},
} as const;

/**
 * Get Public Settings contract
 * GET /system-settings/public
 */
export const getPublicSettingsContract = {
	path: "/system-settings/public",
	contract: {
		method: "GET" as const,
		responses: {
			200: GetPublicSettings200ResponseSchema,
			500: GetPublicSettings500ResponseSchema,
		},
	},
} as const;

/**
 * Update System Setting contract
 * PUT /system-settings/:name
 */
export const setSystemSettingContract = {
	path: "/system-settings/:name",
	contract: {
		method: "PUT" as const,
		params: UpdateSystemSettingPathParamsSchema,
		body: UpdateSystemSettingRequestSchema,
		responses: {
			200: UpdateSystemSetting200ResponseSchema,
			400: UpdateSystemSetting400ResponseSchema,
			500: UpdateSystemSetting500ResponseSchema,
		},
	},
} as const;

/**
 * Bulk Update System Settings contract
 * PUT /system-settings/bulk
 */
export const setBulkSystemSettingsContract = {
	path: "/system-settings/bulk",
	contract: {
		method: "PUT" as const,
		body: UpdateBulkSystemSettingsRequestSchema,
		responses: {
			200: UpdateBulkSystemSettings200ResponseSchema,
			400: UpdateBulkSystemSettings400ResponseSchema,
			500: UpdateBulkSystemSettings500ResponseSchema,
		},
	},
} as const;

/**
 * Delete System Setting contract
 * DELETE /system-settings/:name
 */
export const deleteSystemSettingContract = {
	path: "/system-settings/:name",
	contract: {
		method: "DELETE" as const,
		params: DeleteSystemSettingPathParamsSchema,
		responses: {
			200: DeleteSystemSetting200ResponseSchema,
			400: DeleteSystemSetting400ResponseSchema,
			404: DeleteSystemSetting404ResponseSchema,
			500: DeleteSystemSetting500ResponseSchema,
		},
	},
} as const;

/**
 * =============================================================================
 * BRANDING FILE UPLOAD CONTRACTS
 * =============================================================================
 */

/**
 * Get Favicon contract
 * GET /system-settings/branding/favicon
 */
export const getFaviconContract = {
	path: "/system-settings/branding/favicon",
	contract: {
		method: "GET" as const,
		responses: {
			200: GetFavicon200ResponseSchema,
			500: GetFavicon500ResponseSchema,
		},
	},
} as const;

/**
 * Upload Favicon contract
 * POST /system-settings/branding/favicon
 */
export const setFaviconContract = {
	path: "/system-settings/branding/favicon",
	contract: {
		method: "POST" as const,
		contentType: "multipart/form-data" as const,
		body: z.any(), // File upload handled by multer
		responses: {
			200: UploadFavicon200ResponseSchema,
			400: UploadFavicon400ResponseSchema,
			500: UploadFavicon500ResponseSchema,
		},
	},
} as const;

/**
 * Get Logo contract
 * GET /system-settings/branding/logo
 */
export const getLogoContract = {
	path: "/system-settings/branding/logo",
	contract: {
		method: "GET" as const,
		responses: {
			200: GetLogo200ResponseSchema,
			500: GetLogo500ResponseSchema,
		},
	},
} as const;

/**
 * Upload Logo contract
 * POST /system-settings/branding/logo
 */
export const setLogoContract = {
	path: "/system-settings/branding/logo",
	contract: {
		method: "POST" as const,
		contentType: "multipart/form-data" as const,
		body: z.any(), // File upload handled by multer
		responses: {
			200: UploadLogo200ResponseSchema,
			400: UploadLogo400ResponseSchema,
			500: UploadLogo500ResponseSchema,
		},
	},
} as const;

/**
 * Get Store Picture contract
 * GET /system-settings/branding/store-pic
 */
export const getStorePicContract = {
	path: "/system-settings/branding/store-pic",
	contract: {
		method: "GET" as const,
		responses: {
			200: GetStorePic200ResponseSchema,
			500: GetStorePic500ResponseSchema,
		},
	},
} as const;

/**
 * Upload Store Picture contract
 * POST /system-settings/branding/store-pic
 */
export const setStorePicContract = {
	path: "/system-settings/branding/store-pic",
	contract: {
		method: "POST" as const,
		contentType: "multipart/form-data" as const,
		body: z.any(), // File upload handled by multer
		responses: {
			200: UploadStorePic200ResponseSchema,
			400: UploadStorePic400ResponseSchema,
			500: UploadStorePic500ResponseSchema,
		},
	},
} as const;

/**
 * =============================================================================
 * CONVENIENCE EXPORTS
 * =============================================================================
 */

/**
 * System settings paths as constants
 */
export const systemSettingsPaths = {
	settings: "/system-settings",
	setting: "/system-settings/:name",
	publicSettings: "/system-settings/public",
	bulkSettings: "/system-settings/bulk",
	favicon: "/system-settings/branding/favicon",
	logo: "/system-settings/branding/logo",
	storePic: "/system-settings/branding/store-pic",
} as const;

/**
 * Combined contracts for convenience
 */
export const systemSettingsContracts = {
	getSystemSettings: getSystemSettingsContract,
	getSystemSetting: getSystemSettingContract,
	getPublicSettings: getPublicSettingsContract,
	setSystemSetting: setSystemSettingContract,
	setBulkSystemSettings: setBulkSystemSettingsContract,
	deleteSystemSetting: deleteSystemSettingContract,
	getFavicon: getFaviconContract,
	setFavicon: setFaviconContract,
	getLogo: getLogoContract,
	setLogo: setLogoContract,
	getStorePic: getStorePicContract,
	setStorePic: setStorePicContract,
} as const;

/**
 * Endpoint configuration for query factory
 * Maps contract objects to simplified config format
 */
export const systemSettingsEndpointConfig = {
	getSystemSettings: {
		path: getSystemSettingsContract.path,
		method: getSystemSettingsContract.contract.method,
	},
	getSystemSetting: {
		path: getSystemSettingContract.path,
		method: getSystemSettingContract.contract.method,
	},
	getPublicSettings: {
		path: getPublicSettingsContract.path,
		method: getPublicSettingsContract.contract.method,
	},
	setSystemSetting: {
		path: setSystemSettingContract.path,
		method: setSystemSettingContract.contract.method,
	},
	setBulkSystemSettings: {
		path: setBulkSystemSettingsContract.path,
		method: setBulkSystemSettingsContract.contract.method,
	},
	deleteSystemSetting: {
		path: deleteSystemSettingContract.path,
		method: deleteSystemSettingContract.contract.method,
	},
	getFavicon: {
		path: getFaviconContract.path,
		method: getFaviconContract.contract.method,
	},
	setFavicon: {
		path: setFaviconContract.path,
		method: setFaviconContract.contract.method,
	},
	getLogo: {
		path: getLogoContract.path,
		method: getLogoContract.contract.method,
	},
	setLogo: {
		path: setLogoContract.path,
		method: setLogoContract.contract.method,
	},
	getStorePic: {
		path: getStorePicContract.path,
		method: getStorePicContract.contract.method,
	},
	setStorePic: {
		path: setStorePicContract.path,
		method: setStorePicContract.contract.method,
	},
} as const;
